import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select, SelectItem, Label, Input, DateInput } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * DeviationProductIntegration - Component for managing deviation products within the deviation form
 * Similar to ComplaintProductIntegration but for DeviationProduct
 */
const DeviationProductIntegration = ({ 
  deviationId,
  formData,
  setFormData,
  config,
  isEditMode = false,
  selectedRecord = null // For view mode
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localProducts, setLocalProducts] = useState([]);

  // Determine if we're in view mode (selectedRecord provided but not in edit mode)
  const isViewMode = selectedRecord && !isEditMode;
  const recordId = selectedRecord?.id || deviationId || formData?.id;

  // Fetch existing products if deviationId exists (for both edit and view mode)
  const { data: existingDeviation = null } = useFlexibleList(
    recordId ? `/deviation/${recordId}` : null
  );

  // Track if we've initialized to prevent infinite loops
  const isInitializedRef = React.useRef(false);
  const prevDeviationIdRef = React.useRef(deviationId);
  
  // Use ref to store latest products for immediate access
  const localProductsRef = React.useRef([]);

  // Initialize products only once when component mounts or deviationId changes
  useEffect(() => {
    // Reset initialization flag when deviationId changes
    if (prevDeviationIdRef.current !== deviationId) {
      isInitializedRef.current = false;
      prevDeviationIdRef.current = deviationId;
    }

    // Only initialize if we haven't already done so
    if (isInitializedRef.current) return;

    // Priority: existingDeviation.products (from API) > selectedRecord.products (view mode) > formData.products > empty array
    if (existingDeviation && existingDeviation.products && Array.isArray(existingDeviation.products) && existingDeviation.products.length > 0) {
      // Load existing products from API if available
      setLocalProducts(existingDeviation.products);
      isInitializedRef.current = true;
    } else if (selectedRecord && selectedRecord.products && Array.isArray(selectedRecord.products) && selectedRecord.products.length > 0) {
      // For view mode, use products from selectedRecord
      setLocalProducts(selectedRecord.products);
      isInitializedRef.current = true;
    } else {
      // For new records OR edit mode without existing products, check formData first, then initialize empty array
      if (formData && formData.products && Array.isArray(formData.products) && formData.products.length > 0) {
        setLocalProducts(formData.products);
      } else {
        setLocalProducts([]);
      }
      isInitializedRef.current = true;
    }
  }, [recordId, isEditMode, isViewMode, existingDeviation, selectedRecord, formData?.products]);

  // Update ref whenever localProducts changes
  useEffect(() => {
    localProductsRef.current = localProducts;
  }, [localProducts]);

  // Sync localProducts to formData (but only if we've initialized)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Sync immediately without timeout to ensure data is available when form is submitted
    const syncToFormData = () => {
      setFormData(prev => {
        // Only update if the data actually changed to prevent unnecessary re-renders
        const currentProducts = prev.products || [];
        const currentProductsStr = JSON.stringify(currentProducts);
        const localProductsStr = JSON.stringify(localProducts);
        
        if (currentProductsStr === localProductsStr) {
          return prev;
        }
        
        console.log('🔄 Syncing localProducts to formData:', localProducts);
        
        return {
          ...prev,
          products: localProducts
        };
      });
    };
    
    syncToFormData();
  }, [localProducts, setFormData]);
  
  // Force sync before form submission - expose sync function via window for debugging
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__syncDeviationProducts = () => {
        // Always sync, even if not initialized - use current localProducts state
        const latestProducts = localProductsRef.current.length > 0 
          ? localProductsRef.current 
          : localProducts;
        
        console.log('🔄 Force syncing products to formData:', latestProducts);
        
        // Use functional update to ensure we get the latest state
        setFormData(prev => {
          const updated = {
            ...prev,
            products: latestProducts
          };
          console.log('✅ Force sync complete - updated formData with products:', updated.products);
          // Update the ref as well to ensure consistency
          localProductsRef.current = latestProducts;
          return updated;
        });
      };
      
      // Also expose getter for debugging and direct data access
      window.__getDeviationProducts = () => {
        const latestProducts = localProductsRef.current.length > 0 
          ? localProductsRef.current 
          : localProducts;
        return {
          localProducts: latestProducts,
          localProductsState: localProducts,
          isInitialized: isInitializedRef.current,
          formData: formData.products
        };
      };
    }
  }, [setFormData, formData, localProducts]);

  const addProduct = () => {
    const newProduct = {
      id: `temp-${Date.now()}`, // Temporary ID for new products
      productCode: '',
      productName: '',
      batchNumber: '',
      productQuantity: null,
      expiryDate: null,
      isNew: true // Flag to identify new products
    };
    setLocalProducts(prev => {
      const updated = [...prev, newProduct];
      console.log('🔄 Adding product:', updated);
      // Update ref immediately for consistency
      localProductsRef.current = updated;
      return updated;
    });
    setIsExpanded(true); // Auto-expand when adding new product
  };

  const removeProduct = (index) => {
    setLocalProducts(prev => {
      const newProducts = prev.filter((_, i) => i !== index);
      console.log('🔄 Removing product:', newProducts);
      // Update ref immediately for consistency
      localProductsRef.current = newProducts;
      return newProducts;
    });
  };

  const updateProduct = (index, field, value) => {
    setLocalProducts(prev => {
      const newProducts = [...prev];
      newProducts[index] = {
        ...newProducts[index],
        [field]: value
      };
      console.log('🔄 Updating product:', { index, field, value, newProducts });
      // Update ref immediately for consistency
      localProductsRef.current = newProducts;
      return newProducts;
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📦</span>
          <span className="font-semibold">Products</span>
          {localProducts.length > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              theme.colors.primary,
              theme.text.inverse
            )}>
              {localProducts.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t", theme.border.light)}>
          {localProducts.length === 0 ? (
            <div className={cn("text-center py-8", theme.text.secondary)}>
              <p className="mb-4">No products added yet.</p>
              {!isViewMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProduct}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add First Product
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {localProducts.map((product, index) => (
                <div
                  key={product.id || index}
                  className={cn(
                    "p-4 rounded-lg border space-y-4",
                    theme.border.subtle,
                    theme.colors.backgroundAlt
                  )}
                >
                  {/* Product Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Product #{index + 1}
                      </span>
                    </div>
                    {!isViewMode && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeProduct(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Product Fields - Responsive Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
                    {/* Product Code */}
                    <div className="sm:col-span-1 lg:col-span-2">
                      <Label htmlFor={`productCode-${index}`}>
                        Product Code
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {product.productCode || '—'}
                        </div>
                      ) : (
                        <Input
                          id={`productCode-${index}`}
                          type="text"
                          value={product.productCode || ''}
                          onChange={(e) => updateProduct(index, 'productCode', e.target.value)}
                          placeholder="Enter product code"
                        />
                      )}
                    </div>

                    {/* Product Name */}
                    <div className="sm:col-span-1 lg:col-span-4">
                      <Label htmlFor={`productName-${index}`}>
                        Product Name
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {product.productName || '—'}
                        </div>
                      ) : (
                        <Input
                          id={`productName-${index}`}
                          type="text"
                          value={product.productName || ''}
                          onChange={(e) => updateProduct(index, 'productName', e.target.value)}
                          placeholder="Enter product name"
                        />
                      )}
                    </div>

                    {/* Batch No. */}
                    <div className="sm:col-span-1 lg:col-span-2">
                      <Label htmlFor={`batchNumber-${index}`}>
                        Batch No.
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {product.batchNumber || '—'}
                        </div>
                      ) : (
                        <Input
                          id={`batchNumber-${index}`}
                          type="text"
                          value={product.batchNumber || ''}
                          onChange={(e) => updateProduct(index, 'batchNumber', e.target.value)}
                          placeholder="Enter batch number"
                        />
                      )}
                    </div>

                    {/* Product Quantity */}
                    <div className="sm:col-span-1 lg:col-span-2">
                      <Label htmlFor={`productQuantity-${index}`}>
                        Quantity
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {product.productQuantity || '—'}
                        </div>
                      ) : (
                        <Input
                          id={`productQuantity-${index}`}
                          type="number"
                          value={product.productQuantity || ''}
                          onChange={(e) => updateProduct(index, 'productQuantity', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Enter quantity"
                          min="0"
                        />
                      )}
                    </div>

                    {/* Expiry Date */}
                    <div className="sm:col-span-1 lg:col-span-2">
                      <Label htmlFor={`expiryDate-${index}`}>
                        Expiry Date
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {product.expiryDate ? new Date(product.expiryDate).toLocaleDateString() : '—'}
                        </div>
                      ) : (
                        <DateInput
                          id={`expiryDate-${index}`}
                          value={formatDateForInput(product.expiryDate)}
                          onChange={(e) => {
                            const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                            updateProduct(index, 'expiryDate', dateValue);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Product Button - Only show in edit mode */}
              {!isViewMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProduct}
                  className="w-full"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Another Product
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviationProductIntegration;

